<script lang='ts'>
    import Modal from "../Bootstrap/Modal.svelte";
    import { createEventDispatcher, getContext } from "svelte";
    import RaceDate from "./RaceDate.svelte";
    import ActivityMap from "../Activities/ActivityMap.svelte";
    import type { RacePlan, Signature } from "@/Common/constants";
    import type { Writable } from "svelte/store";
    import { MyFitnessStores } from "@/MyFitness/stores";
    import Am5Loader from "../Util/Am5Loader.svelte";
    import Skeleton from "../Common/Skeleton.svelte";
    import { formatXSSDetails, getCurrentFitnessSignature, type XSSDetails } from "@/Util/utils";
    import type { ProgramAchievability } from "@/Program/modules/ProgramAchievability";
    import { format } from "date-fns";
    import ExerciseImageCarousel from "./ExerciseImageCarousel.svelte";
    import DifficultyDiamonds from "../DifficultyDiamonds.svelte";
    import { format_duration } from "@/time";
    import { _ } from "svelte-i18n";
    import DekGraphic from "./DEKGraphic.svelte";
    import Button from "@/lib/components/ui/button/button.svelte";
    import Separator from "@/lib/components/ui/separator/separator.svelte";
    import { cn } from "@/lib/utils";
    import { ActivityTag } from "@/Common/ActivityTag";
    import { TagWrapper } from "@/Common";
    import ActivityChartNew from "@/Activities/ActivityChartNew.svelte";
    
    export let onAssess: () => void
    export let isRaceLibraryPicker: boolean = false

    // Static data for events from activity model
    export let event_logo_url: string
    export let upcoming_event_date: string = null
    export let name: string
    export let elevation: number
    export let distance: number
    export let description: string
    export let is_achievement: boolean = false
    export let event_tags: string[] = []
    export let path: string
    export let icon: string

    // Calculated assessment data
    export let targetSignature: XSSDetails
    export let programAchievability: ProgramAchievability
    export let plan: Writable<RacePlan>
    export let mpaData

    // Static data for activities from activity model
    export let start_date: string
    export let signature: Signature
    export let images: string[]
    export let map_url: string
    export let street_view: string
    export let chart_url: string
    export let video_id: string
    export let tags: any[]
    export let medal: number
    export let duration: number
    export let difficulty: number
    export let xss: number
    export let xhss: number
    export let xlss: number
    export let xpss: number
    export let kge: number
    export let altitude_polyline: number[]

    const { userParams, trainingAdvice } = getContext<MyFitnessStores>('myFitness')
    let currentSignature: XSSDetails
    $: if(trainingAdvice) currentSignature = getCurrentFitnessSignature($trainingAdvice)

    let raceAchievability = null
    $:  raceAchievability = isRaceLibraryPicker ? programAchievability.raceAchievability : null

    let showModal: () => void
    let closeModal: () => void
    let isModalShown: boolean

    const dispatch = createEventDispatcher()
    let onModalClosed = () => {}

    const activityChartOptions = {
        showHeartRate: false,
        showTargetPower: false,
    }

    enum AchievabilityStatus {
        Achievable,
        NotAchievable,
        AchievableByDate,
    }
    let achievability: AchievabilityStatus
    $: {
        if (!raceAchievability) {
            achievability = AchievabilityStatus.NotAchievable
        } else if ($raceAchievability.readyBy === "now") {
            achievability = AchievabilityStatus.Achievable
        } else if ($raceAchievability.readyBy instanceof Date) {
            achievability = AchievabilityStatus.AchievableByDate
        } else {
            achievability = AchievabilityStatus.NotAchievable
        }
    }
    const getAchievabilityColors = (achievability: AchievabilityStatus, analyzeRunning: boolean) => {
        if (analyzeRunning) {
            return "background-color: #d9d9d9; color: #595959"
        }

        switch (achievability) {
            case AchievabilityStatus.Achievable:
                return "background-color: #e5f6d3; color: #5CA907"
            case AchievabilityStatus.AchievableByDate:
                return "background-color: #ffefbf; color: #A96207"
            case AchievabilityStatus.NotAchievable:
                return "background-color: #f6d3d3; color: #A90707"
        }
    }

    const activityReferedToAs = is_achievement ? "ride" : "race"

    function updateTags(is_achievement: boolean, medal: number, event_tags: string[]) {
        const activityTag = ActivityTag("medium")
        return [
            activityTag.race({is_achievement: isRaceLibraryPicker ? is_achievement : true}),
            !isRaceLibraryPicker ? activityTag.breakthrough({medal}, true) : null,
            ...activityTag.list(event_tags),
        ].filter(tag=>tag)
    }
</script>

{#if !isRaceLibraryPicker}
    <div class="@container">
        <div 
            class="twp gap-5 rounded-md shadow-lg max-w-5xl p-6 border grid grid-cols-1
                @xl:grid-cols-[200px,auto]
                @4xl:grid-rows-[auto,1fr,auto] @4xl:grid-cols-[200px,2fr,1fr]"
        >
            <div class="w-[200px] @xl:row-span-2 @4xl:row-span-3">
                <ExerciseImageCarousel images={[...(images ?? []), map_url, street_view, chart_url]} {video_id} height={112} faIconString={icon}/>
            </div>
    
            <div class="text-base font-bold leading-none">{name}</div>

            <DekGraphic class="@xl:col-span-2 @4xl:col-start-2 @4xl:col-span-1" altitudePoints={altitude_polyline} {distance} {elevation} {kge} />
            
            <div class="space-y-2 row-start-4 @xl:row-start-2 @xl:col-start-2 @4xl:col-start-3 @4xl:row-span-2 @4xl:row-start-1">
                <div class="flex gap-1 p-2 rounded border">
                    <div class="space-y-1 text-center leading-none grow">
                        <div class="text-xertpeak font-bold text-xl leading-none">
                            {signature.pp.toFixed(0)}<span class="leading-none tracking-tight text-xs">W</span>
                        </div>
                        <div class="text-muted-foreground">PP</div>
                    </div>
                    <Separator orientation="vertical"/>
                    <div class="space-y-1 text-center leading-none grow">
                        <div class="text-xerthigh font-bold text-xl leading-none">
                            {(signature.atc / 1000).toFixed(1)}<span class="leading-none tracking-tight text-xs">kJ</span>
                        </div>
                        <div class="text-muted-foreground">HIE</div>
                    </div>
                    <Separator orientation="vertical"/>
                    <div class="space-y-1 text-center leading-none grow">
                        <div class="text-xertlow font-bold text-xl leading-none">
                            {signature.ftp.toFixed(0)}<span class="leading-none tracking-tight text-xs">W</span>
                        </div>
                        <div class="text-muted-foreground">FTP</div>
                    </div>
                </div>
                <div class="flex flex-wrap gap-x-4 gap-y-1">
                    <span class="flex gap-1.5">
                        Date:
                        <span class="font-semibold">{format(new Date(start_date), "LLLL dd, yyyy")}</span>
                    </span>
                    {#if duration}
                        <span class="flex gap-1.5">
                            {$_('sessions.card.duration')}
                            <span class="font-semibold">{format_duration(duration)}</span>
                        </span>
                    {/if}
                    {#if xss}
                        {@const formattedXSSstrings = formatXSSDetails({xss, xhss, xlss, xpss})}
                        <span class="flex gap-1.5">
                            {$_('sessions.card.xss')}{' '}
                            <span class="font-semibold">
                                {formattedXSSstrings.xss}
                                {'('}
                                <span class="text-xertlow">{formattedXSSstrings.xlss}</span>
                                {' | '}
                                <span class="text-xerthigh">{formattedXSSstrings.xhss}</span>
                                {' | '}
                                <span class="text-xertpeak">{formattedXSSstrings.xpss}</span>
                                {')'}
                            </span>
                        </span>
                    {/if}
                    {#if difficulty}
                        <span class="flex gap-1.5">
                            {$_('sessions.card.difficulty')}
                            <DifficultyDiamonds {difficulty}/>
                        </span>
                    {/if}
                </div>
            </div>

            <TagWrapper class="row-start-3 col-start-1 @xl:col-span-2 @4xl:col-start-2 @4xl:col-span-1 self-center" tags={updateTags(is_achievement, medal, event_tags)} />
            <Button class="bg-xertblue hover:bg-xertlime col-span-1 @xl:col-span-2 @xl:justify-self-end @xl:w-60 @4xl:col-span-1" size="sm" 
                on:click={() => {
                    onAssess()
                    dispatch('select')
                }}
            >
                Select
            </Button>
        </div>
    </div>
{/if}

{#if isRaceLibraryPicker}
    <div class="@container">
        <div 
            class={cn("twp gap-5 rounded-md shadow-lg max-w-5xl p-6 border grid",
                "@xl:grid-rows-[min-content,1fr,auto,auto]",
                is_achievement ? "grid-cols-1 @xl:grid-cols-[225px,1fr]" : "grid-cols-[min-content,auto] @xl:grid-cols-[200px,1fr,auto]",
            )}
        >
            <img class="object-cover flex-shrink-0 rounded-md w-full aspect-[200/112] col-span-2 @xl:col-span-1 @xl:row-span-2" src={event_logo_url} alt='event logo' />

            <div class={cn("text-base font-bold leading-none", is_achievement ? "" : "col-start-2")}>{name}</div>

            {#if upcoming_event_date && !is_achievement}
                <RaceDate class="row-start-2 @xl:row-start-1 @xl:col-start-3 @xl:row-span-2 w-20 @xl:w-24" dateClass="text-3xl @xl:text-4xl" monthClass="h-7 text-sm @xl:text-base @xl:h-8" date={new Date(upcoming_event_date)} />
            {/if}

            <DekGraphic class="col-span-2 @xl:col-span-1" altitudePoints={altitude_polyline} {distance} {elevation} {kge} width={225}/>

            <div class="col-span-full">
                {description}
            </div>

            <div class="flex justify-between gap-5 flex-wrap col-span-full">
                <TagWrapper class="self-center" tags={updateTags(is_achievement, medal, event_tags)} />

                <Button class="bg-xertblue hover:bg-xertlime w-full @xl:w-60" size="sm" 
                        on:click={() => {
                            onModalClosed = () => dispatch('changeRace') // Attach callback that resets setup form values when modal is closed
                            onAssess()
                            showModal()
                        }}
                    >
                        Assess
                </Button>
            </div>
               
        </div> 
    </div>
    <Modal
        id="{path}_race_assess" bind:show={showModal} bind:close={closeModal} bind:shown={isModalShown}
        header={false} footer={false} hide_body_padding={true} maxWidthStyleString="800px" {onModalClosed}
    >
        <!-- Every race card has a modal, rendering multiple charts and maps is wasteful, this if statement means we only have 1 modal content in the DOM  -->
        {#if isModalShown}
            {@const analyzeRunning = !(mpaData && targetSignature && $plan.raceRecords)}

            <div class="relative">
                <img class="object-cover w-full aspect-[800/360]" src={event_logo_url} alt='event logo banner' />
                <button on:click={closeModal} class="absolute top-2 right-2 w-[55px] p-0 aspect-square text-white hover:text-[#707377] bg-transparent border-none">
                    <i class="fa-thin text-5xl fa-xmark" />
                </button>
                <button 
                    class="absolute bottom-2 right-2 btn-xfai border-none text-white bg-[--color-xert-lime] hover:bg-[--color-xert-blue]"
                    on:click={() => {
                        onModalClosed = () => {} // Remove the callback so that change race is not fired
                        dispatch('select')
                        closeModal()
                    }}
                >
                    Continue
                </button>
            </div>

            <div class="px-7 py-4 gap-6 flex flex-col">
                <div class="flex flex-col gap-4">

                    <div class="py-3 px-5 rounded-md flex flex-row gap-4 items-center" style={getAchievabilityColors(achievability, analyzeRunning)}>
                        <div class='w-[40px] text-3xl text-center'>
                            {#if analyzeRunning}
                                <i class="fa-light fa-circle-notch fa-spin" />
                            {:else if achievability === AchievabilityStatus.Achievable}
                                <i class="fa-light fa-circle-check" />
                            {:else if achievability === AchievabilityStatus.AchievableByDate}
                                <i class="fa-light fa-circle-exclamation" />
                            {:else}
                                <i class="fa-light fa-circle-xmark" />
                            {/if}
                        </div>
                        <div>
                            {#if analyzeRunning}
                                <span>Determining achievability...</span>
                            {:else if achievability === AchievabilityStatus.Achievable}
                                <span>
                                    Based on your current fitness level, this {activityReferedToAs} is likely achievable.
                                    Other constraints such as your time availability may affect this, click <span class="font-medium">CONTINUE</span> for further assessment.
                                </span>
                            {:else if achievability === AchievabilityStatus.AchievableByDate && $raceAchievability.readyBy instanceof Date}
                                <span>
                                    Based on your current fitness level, this {activityReferedToAs} is not achievable by <span class="font-bold">{is_achievement ? "tomorrow" : format($plan.targetDate, 'LLLL dd, yyyy')}</span>
                                    but should be achievable by <span class="font-bold">{format($raceAchievability.readyBy, 'LLLL dd, yyyy')}</span>.
                                    Other settings such as your target weight may affect this, click <span class="font-medium">CONTINUE</span> for further assessment.      
                                </span>
                            {:else}
                                <span>
                                    This {activityReferedToAs} is not likely achievable.
                                    Other settings such as your target weight may affect this, click <span class="font-medium">CONTINUE</span> for further assessment.
                                </span>
                            {/if}
                        </div>
                    </div>

                    <div class="text-lg font-bold">{name}</div>
                
                    <TagWrapper tags={updateTags(is_achievement, medal, event_tags)} />

                    <div class="flex flex-row gap-4 w-full items-start">
                        {#if !is_achievement}<RaceDate class='min-w-[112px]' date={new Date(upcoming_event_date)} />{/if}
                        <div>{description}</div>
                    </div>

                    <DekGraphic altitudePoints={altitude_polyline} {distance} {elevation} {kge} width={500} height={100}/>
                </div>

                <div class="rounded-md">
                    {#if !analyzeRunning}
                        <ActivityMap mapPolyline={$plan.raceRecords.polyline} />
                    {:else}
                        <Skeleton class="w-full aspect-[3/1]" />
                    {/if}
                </div>

                <Am5Loader>
                    {#if !analyzeRunning}
                        <ActivityChartNew 
                            {mpaData} 
                            seriesVisibility={{
                                difficulty: "startHidden",
                                heartRate: "off",
                                targetPower: "off",
                                speed: "startHidden",
                                cadence: "startHidden",
                            }}
                            startingAxisType = "distance"
                        />
                    {:else}
                        <Skeleton class="w-full aspect-[3/1]" />
                    {/if}
                </Am5Loader>

                <div class="flex flex-col gap-4">
                    <div class="text-lg font-bold">Fitness Signature</div>

                    {#if !analyzeRunning}
                        <div class="dist-elevation">
                            <div>
                                <strong class="text-[--color-xlss]">
                                    {targetSignature.xlss.toFixed(0)}<small class="sm:hidden">W</small><small class="hidden sm:inline">watts</small>
                                </strong>
                                <span>Threshold Power</span>
                            </div>
                            <div>
                                <strong class="text-[--color-xhss]">
                                    {(targetSignature.xhss / 1000).toFixed(1)}<small class="sm:hidden">kJ</small><small class="hidden sm:inline">kJ</small>
                                </strong>
                                <span>High Intensity Energy</span>
                            </div>
                            <div>
                                <strong class="text-[--color-xpss]">
                                    {targetSignature.xpss.toFixed(0)}<small class="sm:hidden">W</small><small class="hidden sm:inline">watts</small>
                                </strong>
                                <span>Peak Power</span>
                            </div>
                        </div>
                    {:else}
                        <Skeleton class="w-full aspect-[5/1]" />
                    {/if}
                </div>
            </div>
        {/if}
    </Modal>
{/if}

<style lang="scss">

    .form-container {
        container: form-container / inline-size;
    }

    .race-card {
        width: 100%;
        height: max-content;
        padding: 22px;
        display: grid;
        gap: 24px;
        border: 1px solid transparent;
        border-radius: 0.4rem;
        -webkit-box-shadow: 0px 0px 14px 2px #D6D6D6; 
        box-shadow: 0px 0px 14px 2px #D6D6D6;
        max-width: 930px;
        grid-template-columns: 200px 200px auto min-content 140px;
        grid-auto-flow: row;
        &.personal-activity {
            grid-template-columns: 200px 380px auto;
        }
    }

    .desc {
        font-size: 12px;
        color: #707377;
        width: 100%;
    }
    
    .dist-elevation {
        display: flex;
        flex-direction: row;
        flex-wrap: nowrap;
        gap: 8px;

        div {
            flex: 1 1 0px;
            border: 1px solid #ECECEC;
            border-radius: 5px;
            text-align: center;
            padding: 10px;

            > * {
                display: block;
            }

            strong {
                font-size: 30px;
                font-weight: bold;
                small {
                    font-size: 12px;
                }
            }
        }
    }

    @container form-container (min-width: 750px) {
        .desc {        
            grid-area: 2 / 1 / 3 / 3 ;
        }
        .seperator {
            grid-area: 1 / 3 / 3 / 4 ;
        }
        .date-and-btn {
            grid-area: 1 / 4 / 3 / 5 ;
        }
        .race-card {
            grid-template-columns: 200px auto min-content 140px;
        }
    }
    @container form-container (max-width: 750px) {
        .race-card {
            display: flex;
            flex-direction: column;
        }
        .seperator {
            display: none;
        }
    }

    .signature {
        display: flex;
        flex-direction: row;
        flex-wrap: nowrap;
        gap: 4px;

        div {
            flex: 1 1 0px;
            text-align: center;
            padding: 10px;

            > * {
                display: block;
            }

            strong {
                font-size: 20px;
                font-weight: bold;
                small {
                    font-size: 12px;
                }
            }
        }
        
        hr {
            border-right: 1px solid #E4E4E4;
            width: 1px;
            height: calc(100% - 20px);
            margin: 0px;
            margin-top: 10px;
        }
    }

    .npeak {    
        color: #F00;
    }
    .nlow {
        color: #0B5DBC;
    }
    .nhigh {
        color: #F5A623;
    }
</style>